// Google Analytics 4 + Google Ads (gtag.js) with Consent Mode v2.
//
// INERT unless VITE_GA4_ID and/or VITE_GADS_ID is set at build time — with no
// IDs configured nothing loads, no cookies are set, and the consent banner stays
// hidden (see useCookieConsent). This lets the plumbing ship before the GA4 /
// Google Ads accounts exist.
//
// No inline <script> is used (the SWA CSP forbids inline script): the gtag shim
// and config run from the bundle ('self'); only the gtag.js loader is external,
// from https://www.googletagmanager.com (whitelisted in CSP script-src).
import { safeLocalGetIn } from "src/utils/storage";
import { isStagingHost } from "src/utils/apiUrl";
import { scrubToken } from "src/utils/scrubToken";

const GA4_ID = import.meta.env.VITE_GA4_ID || ""; // e.g. G-XXXXXXXXXX
const GADS_ID = import.meta.env.VITE_GADS_ID || ""; // e.g. AW-XXXXXXXXX
const GADS_LEAD_LABEL = import.meta.env.VITE_GADS_LEAD_LABEL || ""; // Ads conversion label

export const CONSENT_KEY = "betonijerry.consent"; // "granted" | "denied"
export const CONSENT_VALUES = ["granted", "denied"];

// Production-host gate. Staging and prod share ONE build artifact (slot swap), so
// the IDs baked from .env.production also ship in the staging bundle. Restrict all
// analytics/ads firing to the real betonijerry.fi origin so staging tests and
// localhost never pollute the production GA4 property or fire real Ads conversions.
function isProductionHost() {
    if (typeof window === "undefined") return false;
    const h = window.location?.hostname || "";
    if (!h || h === "localhost" || h === "127.0.0.1") return false;
    return !isStagingHost(h);
}

export const analyticsEnabled = Boolean(GA4_ID || GADS_ID) && isProductionHost();

// gtag shim — always safe to call. Pushes to dataLayer; harmless no-op until/
// unless gtag.js has loaded and starts draining the queue.
export function gtag() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
}

let booted = false;

// Map a granted/denied decision onto all four Consent Mode v2 signals.
export function applyConsent(granted) {
    const state = granted ? "granted" : "denied";
    gtag("consent", "update", {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state,
    });
}

// Called once at startup (main.jsx), before React renders.
export function initAnalytics() {
    if (booted || !analyticsEnabled || typeof window === "undefined") return;
    booted = true;

    // 1) Consent Mode v2 default = DENIED, set before any tag fires. Restore a
    //    prior "granted" choice so returning visitors are not re-prompted.
    gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        wait_for_update: 500,
    });
    if (safeLocalGetIn(CONSENT_KEY, CONSENT_VALUES) === "granted") applyConsent(true);

    // 2) Load the gtag.js loader (CSP-whitelisted host).
    const primaryId = GA4_ID || GADS_ID;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
    document.head.appendChild(s);

    // Scrub the preview-page capability token from the location gtag.js
    // auto-attaches to every subsequent hit on this page — including the
    // `config` pings right below, which fire even under Consent Mode denied
    // (cookieless) — e.g. an email CTA landing directly on
    // /tarjouspyynnot/esikatselu?token=<jwt>.
    gtag("set", {
        page_location: scrubToken(window.location.href),
        page_path: scrubToken(window.location.pathname + window.location.search),
    });

    gtag("js", new Date());
    // SPA: suppress gtag's automatic page_view; we emit them on route change.
    if (GA4_ID) gtag("config", GA4_ID, { send_page_view: false });
    if (GADS_ID) gtag("config", GADS_ID);
}

// SPA pageview — call on each route change. Scrub the preview-page capability
// token (?token=<jwt>) from both fields before it reaches GA4.
export function trackPageview(path) {
    if (!analyticsEnabled || typeof window === "undefined") return;
    // Refresh the boot-time `set` fields so later trackEvent/trackConversion
    // hits report the live SPA route instead of the landing URL — runs even
    // GA4-less so Ads-only conversions carry the right page too.
    gtag("set", {
        page_location: scrubToken(window.location.href),
        page_path: scrubToken(path),
    });
    if (!GA4_ID) return;
    gtag("event", "page_view", {
        page_path: scrubToken(path),
        page_location: scrubToken(window.location.href),
    });
}

export function trackEvent(name, params = {}) {
    if (!analyticsEnabled) return;
    gtag("event", name, params);
}

// The primary campaign conversion: a sent tarjouspyyntö (lead).
export function trackConversion(params = {}) {
    if (!analyticsEnabled) return;
    if (GA4_ID) gtag("event", "generate_lead", params);
    if (GADS_ID && GADS_LEAD_LABEL) {
        gtag("event", "conversion", { send_to: `${GADS_ID}/${GADS_LEAD_LABEL}`, ...params });
    }
}
